import { Placement } from '@floating-ui/react';

import { FontDefinition } from '@/types/FontDefinition';
import { GlobalStyles } from '@/types/GlobalStyles';
import { StyleGroupSetting } from '@/types/StyleGroupSetting';
import { StyleGroupSettingType } from '@/types/StyleGroupSettingType';

export const DATA_ATTRIBUTE_SECTION_LIST = 'data-section-list-id';

export const enum MessageAction {
  DisablePreviewEditing = 'DISABLE_PREVIEW_EDITING',
  OpenGlobalStylesMenu = 'OPEN_GLOBAL_STYLES_MENU',
  PageSaved = 'PAGE_SAVED',
  PresetLoaded = 'PRESET_LOADED',
  PresetSaved = 'PRESET_SAVED',
  PresetSavedSuccess = 'PRESET_SAVED_SUCCESS',
  PresetSavedFailure = 'PRESET_SAVED_FAILURE',
  PreviewLoaded = 'PREVIEW_LOADED',
  RequestCloseGlobalStylesMenu = 'REQUEST_CLOSE_GLOBAL_STYLES_MENU',
  SectionUpdated = 'SECTION_UPDATED',
  SectionRemoved = 'SECTION_REMOVED',
  SidebarSectionUpdated = 'SIDEBAR_SECTION_UPDATED',
  StylesUpdated = 'STYLES_UPDATED',
  SiteLayoutUpdated = 'SITE_LAYOUT_UPDATED',
  SectionAdded = 'SECTION_ADDED',
}

export const SECTION_BACKGROUND_TYPE = 'background';
export const SECTION_LAYOUT_TYPE = 'layout';
export const SECTION_STYLE_OVERRIDE_TYPE = 'override_section_style';
export const SECTION_TEXT_COLOR_TYPE = 'color';

export const FONT_SETTINGS: StyleGroupSetting[] = [
  {
    label: 'Font family',
    type: StyleGroupSettingType.Group,
  },
  {
    key: 'secondaryFontFamily',
    label: 'Title font',
    type: StyleGroupSettingType.Font,
  },
  {
    key: 'primaryFontFamily',
    label: 'Body & subtitle',
    type: StyleGroupSettingType.Font,
  },
  {
    label: 'Font weight',
    type: StyleGroupSettingType.Group,
  },
  {
    key: 'titleFontWeight',
    label: 'Title font weight',
    type: StyleGroupSettingType.Weight,
  },
  {
    key: 'subtitleFontWeight',
    label: 'Subtitle font weight',
    type: StyleGroupSettingType.Weight,
  },
  {
    key: 'bodyFontWeight',
    label: 'Body font weight',
    type: StyleGroupSettingType.Weight,
  },
  {
    key: 'buttonFontWeight',
    label: 'Button font weight',
    type: StyleGroupSettingType.Weight,
  },
];

// feat.website-brand-matching
export const UPDATED_FONT_COPY_SETTINGS: StyleGroupSetting[] = [
  {
    label: 'Font family',
    type: StyleGroupSettingType.Group,
  },
  {
    key: 'secondaryFontFamily',
    label: 'Heading font',
    type: StyleGroupSettingType.Font,
  },
  {
    key: 'primaryFontFamily',
    label: 'Subheading, paragraph, button font',
    type: StyleGroupSettingType.Font,
  },
  {
    label: 'Font weight',
    type: StyleGroupSettingType.Group,
  },
  {
    key: 'titleFontWeight',
    label: 'Heading font weight',
    type: StyleGroupSettingType.Weight,
  },
  {
    key: 'subtitleFontWeight',
    label: 'Subheading font weight',
    type: StyleGroupSettingType.Weight,
  },
  {
    key: 'bodyFontWeight',
    label: 'Paragraph font weight',
    type: StyleGroupSettingType.Weight,
  },
  {
    key: 'buttonFontWeight',
    label: 'Button font weight',
    type: StyleGroupSettingType.Weight,
  },
];

export const COLOR_SETTINGS: StyleGroupSetting[] = [
  {
    label: 'Primary section',
    type: StyleGroupSettingType.Group,
  },
  {
    key: 'primaryBgColor',
    label: 'Primary background color',
    type: StyleGroupSettingType.Color,
  },
  {
    key: 'primaryTextColor',
    label: 'Primary text color',
    type: StyleGroupSettingType.Color,
  },
  {
    label: 'Secondary section',
    type: StyleGroupSettingType.Group,
  },
  {
    key: 'secondaryBgColor',
    label: 'Secondary background color',
    type: StyleGroupSettingType.Color,
  },
  {
    key: 'secondaryTextColor',
    label: 'Secondary text color',
    type: StyleGroupSettingType.Color,
  },
  {
    label: 'Accent',
    type: StyleGroupSettingType.Group,
  },
  {
    key: 'accentBgColor',
    label: 'Accent background color',
    type: StyleGroupSettingType.Color,
  },
  {
    key: 'accentTextColor',
    label: 'Accent text color',
    type: StyleGroupSettingType.Color,
  },
  {
    label: 'Primary button',
    type: StyleGroupSettingType.Group,
  },
  {
    key: 'ctaBgColor',
    label: 'Background color',
    type: StyleGroupSettingType.Color,
  },
  {
    key: 'ctaTextColor',
    label: 'Button text color',
    type: StyleGroupSettingType.Color,
  },
  {
    label: 'Secondary button',
    type: StyleGroupSettingType.Group,
  },
  {
    key: 'secondaryCtaBgColor',
    label: 'Button background color',
    type: StyleGroupSettingType.Color,
  },
  {
    key: 'secondaryCtaTextColor',
    label: 'Button text color',
    type: StyleGroupSettingType.Color,
  },
  {
    label: 'Inputs',
    type: StyleGroupSettingType.Group,
  },
  {
    key: 'inputTextColor',
    label: 'Form text color',
    type: StyleGroupSettingType.Color,
  },
  {
    key: 'inputBorderColor',
    label: 'Form border color',
    type: StyleGroupSettingType.Color,
  },
  {
    key: 'inputErrorColor',
    label: 'Error color',
    type: StyleGroupSettingType.Color,
  },
];

export const BORDER_RADIUS_SETTINGS: StyleGroupSetting[] = [
  {
    key: 'buttonBorderRadius',
    label: 'Button border radius',
    type: StyleGroupSettingType.Radius,
  },
  {
    key: 'surfaceBorderRadius',
    label: 'Surface border radius',
    type: StyleGroupSettingType.Radius,
  },
  {
    key: 'inputBorderRadius',
    label: 'Form input border radius',
    type: StyleGroupSettingType.Radius,
  },
];

// feature.website-brand-matching
export const CORNER_SETTINGS: StyleGroupSetting[] = [
  {
    key: 'buttonBorderRadius',
    label: 'Button corner rounding',
    type: StyleGroupSettingType.Radius,
  },
  {
    key: 'surfaceBorderRadius',
    label: 'Surface corner rounding',
    type: StyleGroupSettingType.Radius,
  },
  {
    key: 'inputBorderRadius',
    label: 'Form input corner rounding',
    type: StyleGroupSettingType.Radius,
  },
];

export const FONT_WEIGHTS: { [key: string]: string } = {
  '100': 'Thin',
  '200': 'Extra Light',
  '300': 'Light',
  '400': 'Regular',
  '500': 'Medium',
  '600': 'Semi Bold',
  '700': 'Bold',
  '800': 'Extra Bold',
  '900': 'Black',
};

export const FONTS: FontDefinition[] = [
  {
    name: 'Abel',
    type: 'sans-serif',
    weights: [400],
  },
  {
    name: 'Atkinson Hyperlegible',
    type: 'sans-serif',
    weights: [400, 700],
  },
  {
    name: 'Barlow Condensed',
    type: 'sans-serif',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Cardo',
    type: 'serif',
    weights: [400, 700],
  },
  {
    name: 'Cormorant',
    type: 'serif',
    weights: [300, 400, 500, 600, 700],
  },
  {
    name: 'DM Sans',
    type: 'sans-serif',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Fira Sans',
    type: 'sans-serif',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Inter',
    type: 'sans-serif',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Jost',
    type: 'sans-serif',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Lato',
    type: 'sans-serif',
    weights: [100, 300, 400, 700, 900],
  },
  {
    name: 'Lexend',
    type: 'sans-serif',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Libre Caslon Display',
    type: 'serif',
    weights: [400],
  },
  {
    name: 'Libre Baskerville',
    type: 'serif',
    weights: [400, 700],
  },
  {
    name: 'Lora',
    type: 'serif',
    weights: [400, 500, 600, 700],
  },
  {
    name: 'Marcellus',
    type: 'serif',
    weights: [400],
  },
  {
    name: 'Montserrat',
    type: 'serif',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Open Sans',
    type: 'sans-serif',
    weights: [300, 400, 500, 600, 700, 800],
  },
  {
    name: 'Oswald',
    type: 'sans-serif',
    weights: [200, 300, 400, 500, 600, 700],
  },
  {
    name: 'Playfair',
    type: 'serif',
    weights: [300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Playfair Display',
    type: 'serif',
    weights: [400, 500, 600, 700, 800, 900],
  },
  {
    name: 'PT Sans',
    type: 'sans-serif',
    weights: [400, 700],
  },
  {
    name: 'PT Serif',
    type: 'serif',
    weights: [400, 700],
  },
  {
    name: 'Quicksand',
    type: 'sans-serif',
    weights: [300, 400, 500, 600, 700],
  },
  {
    name: 'Rajdhani',
    type: 'sans-serif',
    weights: [300, 400, 500, 600, 700],
  },
  {
    name: 'Raleway',
    type: 'sans-serif',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Readex Pro',
    type: 'sans-serif',
    weights: [200, 300, 400, 500, 600, 700],
  },
  {
    name: 'Roboto',
    type: 'serif',
    weights: [100, 300, 400, 500, 700, 900],
  },
  {
    name: 'Roboto Slab',
    type: 'serif',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Rubik',
    type: 'sans-serif',
    weights: [300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Sen',
    type: 'sans-serif',
    weights: [400, 500, 600, 700, 800],
  },
  {
    name: 'Source Sans Pro',
    type: 'sans-serif',
    weights: [200, 300, 400, 600, 700, 900],
  },
  {
    name: 'Teachers',
    type: 'serif',
    weights: [400, 500, 600, 700, 800],
  },
  {
    name: 'Teko',
    type: 'sans-serif',
    weights: [300, 400, 500, 600, 700],
  },
  {
    name: 'Tilt Warp',
    type: 'sans-serif',
    weights: [400],
  },
  {
    name: 'Work Sans',
    type: 'sans-serif',
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    name: 'Yellowtail',
    type: 'cursive',
    weights: [400],
  },
];

export const PUBLISH_BUTTON_TEXT = 'Publish changes';
export const SAVE_BUTTON_TEXT = 'Save';
export const GO_BACK_BUTTON_TEXT = 'Go back';

export const Z_INDEX_SETTING_POPOVER = 9999;
export const Z_INDEX_SETTING_POPOVER_OVERLAY = Z_INDEX_SETTING_POPOVER - 1;

export const NETWORK_STATUS = {
  UNAUTHORIZED: 401,
};

export const APP_NAME = 'croissant';

export const ALLOWED_PLACEMENTS: Placement[] = [
  'top',
  'top-start',
  'top-end',
  'right',
  'right-start',
  'right-end',
  'bottom',
  'bottom-start',
  'bottom-end',
  'left',
  'left-start',
  'left-end',
];

export const ADD_SECTION_MODAL_TITLE = 'Add a section';
export const INSERT_SECTION_TOOLTIP = 'Insert section';

export const MAX_TOTAL_SECTIONS = 25;
export const MAX_SECTIONS_REACHED = `${MAX_TOTAL_SECTIONS} sections max. Delete a section to add another`;

export const DELETE_SECTION_MODAL_TITLE = (sectionName: string) =>
  `Delete "${sectionName}" section`;
export const DELETE_SECTION_MODAL_TEXT = 'This cannot be undone.';

export enum Preset {
  Default = 'default',
  Cheerful = 'cheerful',
  Neutral = 'neutral',
  Sweet = 'sweet',
  Contemporary = 'contemporary',
  Professional = 'professional',
}

type GlobalStylesPresets = {
  [key in Preset]: Partial<GlobalStyles>;
};

// For screen readers to give a short description of the theme preset
export const GLOBAL_STYLE_PRESET_A11Y_DESCRIPTIONS: {
  [key in Preset]: string;
} = {
  [Preset.Cheerful]:
    'Cheerful theme preset styled with bright pops of yellow and orange.',
  [Preset.Neutral]:
    'Neutral theme preset styled with muted green-gray and sand tones.',
  [Preset.Sweet]:
    'Sweet theme preset styled with soft pink, blue, and lavender tones.',
  [Preset.Contemporary]:
    'Contemporary theme preset styled with with contrasting black, white, and grey.',
  [Preset.Professional]:
    'Professional theme preset styled with a clean white background with blue and black accent colours.',
  [Preset.Default]:
    'Blank theme preset styled with neutral white, black and light grey tones.',
};

export const GLOBAL_STYLE_PRESETS: GlobalStylesPresets = {
  [Preset.Cheerful]: {
    accentBgColor: '#E8E8E8',
    accentCtaBgColor: '#F15A01',
    accentCtaTextColor: '#FFFFFF',
    accentTextColor: '#12151A',
    bodyFontWeight: '400',
    buttonBorderRadius: '12px',
    buttonFontWeight: '400',
    ctaBgColor: '#F15A01',
    ctaTextColor: '#FFFFFF',
    inputBorderColor: '#9B9B9B',
    inputBorderRadius: '12px',
    inputErrorColor: '#D83232',
    inputTextColor: '12151A',
    primaryBgColor: '#FCDC92',
    primaryFontFamily: 'Lato',
    primaryTextColor: '#12151A',
    promotionBadgeBgColor: 'EF2F3D',
    promotionBadgeTextColor: 'FFFFFF',
    promotionTextColor: 'EF2F3D',
    secondaryBgColor: '#FFFFFF',
    secondaryCtaBgColor: '#F15A01',
    secondaryCtaTextColor: '#FFFFFF',
    secondaryFontFamily: 'Quicksand',
    secondaryTextColor: '#12151A',
    subtitleFontWeight: '400',
    surfaceBorderRadius: '20px',
    titleFontWeight: '400',
  },
  [Preset.Neutral]: {
    accentBgColor: '#FBF7E4',
    accentCtaBgColor: '#405858',
    accentCtaTextColor: '#FFFFFF',
    accentTextColor: '#272F2F',
    bodyFontWeight: '400',
    buttonBorderRadius: '4px',
    buttonFontWeight: '400',
    ctaBgColor: '#405858',
    ctaTextColor: '#FFFFFF',
    inputBorderColor: '#272F2F',
    inputBorderRadius: '4px',
    inputErrorColor: '#D83232',
    inputTextColor: '#36394D',
    primaryBgColor: '#9FAFAF',
    primaryFontFamily: 'Rubik',
    primaryTextColor: '#272F2F',
    promotionBadgeBgColor: '#EF2F3D',
    promotionBadgeTextColor: '#FFFFFF',
    promotionTextColor: '#EF2F3D',
    secondaryBgColor: '#FFFFFF',
    secondaryCtaBgColor: '#405858',
    secondaryCtaTextColor: '#FFFFFF',
    secondaryFontFamily: 'Rubik',
    secondaryTextColor: '#272F2F',
    subtitleFontWeight: '300',
    surfaceBorderRadius: '8px',
    titleFontWeight: '400',
  },
  [Preset.Sweet]: {
    accentBgColor: '#EEE5F0',
    accentCtaBgColor: '#AE4A70',
    accentCtaTextColor: '#FFFFFF',
    accentTextColor: '#482F6F',
    bodyFontWeight: '300',
    buttonBorderRadius: '12px',
    buttonFontWeight: '400',
    ctaBgColor: '#AE4A70',
    ctaTextColor: '#FFFFFF',
    inputBorderColor: '#482F6F',
    inputBorderRadius: '12px',
    inputErrorColor: '#D83232',
    inputTextColor: '#482F6F',
    primaryBgColor: '#FFF3F2',
    primaryFontFamily: 'DM Sans',
    primaryTextColor: '#482F6F',
    promotionBadgeBgColor: '#EF2F3D',
    promotionBadgeTextColor: '#FFFFFF',
    promotionTextColor: '#EF2F3D',
    secondaryBgColor: '#FFFFFF',
    secondaryCtaBgColor: '#AE4A70',
    secondaryCtaTextColor: '#FFFFFF',
    secondaryFontFamily: 'Playfair',
    secondaryTextColor: '#482F6F',
    subtitleFontWeight: '400',
    surfaceBorderRadius: '20px',
    titleFontWeight: '400',
  },
  [Preset.Contemporary]: {
    accentBgColor: '#000000',
    accentCtaBgColor: '#FFFFFF',
    accentCtaTextColor: '#000000',
    accentTextColor: '#FFFFFF',
    bodyFontWeight: '300',
    buttonBorderRadius: '0px',
    buttonFontWeight: '400',
    ctaBgColor: '#000000',
    ctaTextColor: '#FFFFFF',
    inputBorderColor: '#000000',
    inputBorderRadius: '0px',
    inputErrorColor: '#D83232',
    inputTextColor: '#000000',
    primaryBgColor: '#EAEAEC',
    primaryFontFamily: 'Roboto',
    primaryTextColor: '#000000',
    promotionBadgeBgColor: '#EF2F3D',
    promotionBadgeTextColor: '#FFFFFF',
    promotionTextColor: '#EF2F3D',
    secondaryBgColor: '#FFFFFF',
    secondaryCtaBgColor: '#000000',
    secondaryCtaTextColor: '#FFFFFF',
    secondaryFontFamily: 'Libre Caslon Display',
    secondaryTextColor: '#000000',
    subtitleFontWeight: '300',
    surfaceBorderRadius: '0px',
    titleFontWeight: '400',
  },
  [Preset.Professional]: {
    accentBgColor: '#0465FF',
    accentCtaBgColor: '#101D2C',
    accentCtaTextColor: '#FFFFFF',
    accentTextColor: '#FFFFFF',
    bodyFontWeight: '400',
    buttonBorderRadius: '4px',
    buttonFontWeight: '400',
    ctaBgColor: '#0465FF',
    ctaTextColor: '#FFFFFF',
    inputBorderColor: '#8B8E93',
    inputBorderRadius: '4px',
    inputErrorColor: '#D83232',
    inputTextColor: '#36394D',
    primaryBgColor: '#FFFFFF',
    primaryFontFamily: 'Lato',
    primaryTextColor: '#101D2C',
    promotionBadgeBgColor: '#EF2F3D',
    promotionBadgeTextColor: '#FFFFFF',
    promotionTextColor: '#FFFFFF',
    secondaryBgColor: '#101D2C',
    secondaryCtaBgColor: '#0465FF',
    secondaryCtaTextColor: '#FFFFFF',
    secondaryFontFamily: 'Readex Pro',
    secondaryTextColor: '#FFFFFF',
    subtitleFontWeight: '400',
    surfaceBorderRadius: '4px',
    titleFontWeight: '400',
  },
  // Note that this is a direct copy of the monolith's TenantGlobalStyles::DEFAULT_GLOBAL_STYLES
  // The downside of storing a reference to this preset in Croissant is any change to the default styles will require a change in two (technically three, if we count Site Template Core) repos
  [Preset.Default]: {
    accentBgColor: '#36394D',
    accentCtaBgColor: '#F2F3F5',
    accentCtaTextColor: '#36394D',
    accentTextColor: '#F2F3F5',
    bodyFontWeight: '400',
    buttonBorderRadius: '0',
    buttonFontWeight: '400',
    ctaBgColor: '#36394D',
    ctaTextColor: '#FFFFFF',
    inputBorderColor: '#CACCD6',
    inputBorderRadius: '0',
    inputErrorColor: '#D83232',
    inputTextColor: '#36394D',
    primaryBgColor: '#FFFFFF',
    primaryFontFamily: 'Jost, sans-serif',
    primaryTextColor: '#36394D',
    promotionBadgeBgColor: '#EF2F3D',
    promotionBadgeTextColor: '#FFFFFF',
    promotionTextColor: '#EF2F3D',
    secondaryBgColor: '#F2F3F5',
    secondaryCtaBgColor: '#36394D',
    secondaryCtaTextColor: '#FFFFFF',
    secondaryFontFamily: 'Jost, sans-serif',
    secondaryTextColor: '#36394D',
    subtitleFontWeight: '400',
    surfaceBorderRadius: '0',
    titleFontWeight: '400',
  },
};
